import React, { useState } from 'react';
import { ChevronLeft, Copy, Volume2, FileDown } from 'lucide-react';
import { Summary, QuickSummaryContent, QuickSummaryBulletPoint, QuickSummaryTable } from '@/features/summaries/types';
import { useToast } from '@/hooks/use-toast';

interface QuickSummaryViewerProps {
  summary: Summary;
  onBack: () => void;
}

export const QuickSummaryViewer: React.FC<QuickSummaryViewerProps> = ({ summary, onBack }) => {
  const [copiedText, setCopiedText] = useState('');
  const { toast } = useToast();

  // Parse summary content
  const parsedContent: QuickSummaryContent = React.useMemo(() => {
    if (!summary.content) {
      return { sections: [] };
    }

    try {
      const parsed = JSON.parse(summary.content);
      if (parsed.sections && Array.isArray(parsed.sections)) {
        return parsed as QuickSummaryContent;
      }
    } catch {
      // If not JSON, treat as plain text and create a single section
    }

    // Fallback: split plain text content into bullet points
    const lines = summary.content.split('\n').filter(line => line.trim());
    return {
      sections: [{
        bulletPoints: lines.map(line => ({
          text: line.replace(/^[-•]\s*/, ''),
        }))
      }]
    };
  }, [summary]);

  const handleCopy = () => {
    const content = document.querySelector('.quick-summary-content')?.textContent || '';
    navigator.clipboard.writeText(content);
    setCopiedText('Copied!');
    setTimeout(() => setCopiedText(''), 2000);
  };

  const handleAudio = () => {
    toast({
      title: "Coming soon",
      description: "Text-to-speech will be available soon.",
    });
  };

  const handleSaveNote = () => {
    toast({
      title: "Coming soon",
      description: "Save as note will be available soon.",
    });
  };

  const handleDownload = () => {
    const content = document.querySelector('.quick-summary-content')?.textContent || '';
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${summary.title}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const renderTextWithBold = (text: string) => {
    const parts = text.split(/(<strong>.*?<\/strong>)/g);
    return parts.map((part, index) => {
      if (part.startsWith('<strong>') && part.endsWith('</strong>')) {
        const content = part.replace(/<\/?strong>/g, '');
        return <strong key={index} className="font-semibold">{content}</strong>;
      }
      return <span key={index}>{part}</span>;
    });
  };

  // Convert number to letter label (0 -> A., 1 -> B., etc.)
  const toLetterLabel = (num: number): string => {
    return String.fromCharCode(65 + num) + '.';
  };

  const renderBulletPoint = (bullet: QuickSummaryBulletPoint, index: number, depth: number = 0) => {
    const hasChildren = bullet.children && bullet.children.length > 0;
    
    return (
      <li 
        key={index} 
        className={depth === 0 
          ? "leading-7 text-foreground/90" 
          : "leading-7 text-foreground/80"
        }
      >
        {renderTextWithBold(bullet.text)}
        {hasChildren && (
          <ul className="list-outside list-disc pl-5 mt-1 space-y-1">
            {bullet.children!.map((child, childIndex) => 
              renderBulletPoint(child, childIndex, depth + 1)
            )}
          </ul>
        )}
      </li>
    );
  };

  const renderTable = (table: QuickSummaryTable) => {
    return (
      <div className="my-6 overflow-x-auto rounded-lg border border-border/50 dark:border-primary/10">
        <table className="min-w-full divide-y divide-border/50 dark:divide-border/30">
          <thead className="bg-muted/80">
            <tr className="border-border border-b">
              {table.headers.map((header, index) => (
                <th 
                  key={index}
                  className="border-b border-r border-border/50 bg-muted/50 px-4 py-3 text-left text-sm font-semibold last:border-r-0 dark:border-primary/10 text-foreground"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border bg-muted/40">
            {table.rows.map((row, rowIndex) => (
              <tr key={rowIndex} className="border-border border-b">
                {row.cells.map((cell, cellIndex) => (
                  <td 
                    key={cellIndex}
                    className="border-b border-r border-border/50 px-4 py-3 text-sm last:border-r-0 dark:border-primary/10 text-foreground/90"
                  >
                    {cell.isBold ? (
                      <strong className="font-semibold">{renderTextWithBold(cell.text)}</strong>
                    ) : (
                      renderTextWithBold(cell.text)
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="flex h-full flex-col bg-background">
      {/* Back Button - Sticky */}
      <div className="sticky top-0 z-10 shrink-0 bg-background px-4 pb-2 pt-4">
        <button
          onClick={onBack}
          className="flex h-fit w-fit items-center gap-2 rounded-lg bg-transparent p-0 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          <ChevronLeft className="h-4 w-4" />
          <span className="text-sm">Back</span>
        </button>
      </div>

      {/* Content Area */}
      <div className="relative flex-1 overflow-y-auto">
        <div className="px-4">
          <div className="quick-summary-content prose prose-neutral dark:prose-invert max-w-none">
            <div className="space-y-4">
              {parsedContent.sections.length > 0 ? (
                parsedContent.sections.map((section, sectionIndex) => (
                  <div key={sectionIndex} className="space-y-2">
                    {/* Section Title with Letter Label */}
                    {section.title && (
                      <h3 className="text-base font-semibold text-foreground mt-4 first:mt-0">
                        {toLetterLabel(sectionIndex)} {section.title}
                      </h3>
                    )}

                    {/* Bullet Points */}
                    {section.bulletPoints && section.bulletPoints.length > 0 && (
                      <ul className="list-outside list-disc pl-5 space-y-2">
                        {section.bulletPoints.map((bullet, bulletIndex) => 
                          renderBulletPoint(bullet, bulletIndex)
                        )}
                      </ul>
                    )}

                    {/* Table */}
                    {section.table && renderTable(section.table)}
                  </div>
                ))
              ) : (
                <p className="text-muted-foreground">No content available.</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Action Bar - Bottom */}
      <div className="py-3 px-4 bg-background">
        <div className="flex flex-row items-center justify-between space-x-1">
          <div className="flex flex-row items-center space-x-1">
            {/* Copy Button */}
            <button
              onClick={handleCopy}
              className="inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium transition-colors hover:bg-muted hover:text-foreground h-7 w-7 p-1.5 text-muted-foreground"
              title="Copy to clipboard"
            >
              <Copy className="h-4 w-4" />
            </button>

            {/* Audio Button */}
            <button
              onClick={handleAudio}
              className="inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium transition-colors hover:bg-muted hover:text-foreground h-7 w-7 p-1.5 text-muted-foreground"
              title="Read aloud"
            >
              <Volume2 className="h-4 w-4" />
            </button>

            {/* Save as Note Button */}
            <button
              onClick={handleSaveNote}
              className="inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium transition-colors hover:bg-muted hover:text-foreground h-7 w-7 p-1.5 text-muted-foreground"
              title="Save as note"
            >
              <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 256 256" className="h-4 w-4" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg">
                <path d="M84,108A12,12,0,0,1,96,96h64a12,12,0,0,1,0,24H96A12,12,0,0,1,84,108Zm32,28H96a12,12,0,0,0,0,24h20a12,12,0,0,0,0-24ZM228,48V156.69a19.86,19.86,0,0,1-5.86,14.14l-51.31,51.31A19.86,19.86,0,0,1,156.69,228H48a20,20,0,0,1-20-20V48A20,20,0,0,1,48,28H208A20,20,0,0,1,228,48ZM52,204h92V156a12,12,0,0,1,12-12h48V52H52Zm139-36H168v23Z"></path>
              </svg>
            </button>

            {/* Download Button */}
            <button
              onClick={handleDownload}
              className="inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium transition-colors hover:bg-muted hover:text-foreground h-7 w-7 p-1.5 text-muted-foreground"
              title="Download"
            >
              <FileDown className="h-4 w-4" />
            </button>
          </div>

          {/* Copy confirmation text */}
          {copiedText && (
            <span className="text-xs text-correct animate-in fade-in-0">
              {copiedText}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuickSummaryViewer;